export const muscleGroups = ['Грудь', 'Спина', 'Ноги', 'Плечи', 'Руки', 'Кор', 'Другое'] as const;
export type MuscleGroup = (typeof muscleGroups)[number];

export type UUID = string;
export type ISODateTime = string;
export type LocalDate = string;

export interface TimestampedEntity {
  id: UUID;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface Exercise extends TimestampedEntity {
  name: string;
  normalizedName: string;
  muscleGroup: MuscleGroup;
  isCustom: boolean;
  isArchived: boolean;
}

export interface WorkoutPlan extends TimestampedEntity {
  name: string;
  notes: string | null;
  isArchived: boolean;
  templateKey?: 'full-body-a' | 'full-body-b';
}

export interface PlanExercise extends TimestampedEntity {
  planId: UUID;
  exerciseId: UUID;
  orderIndex: number;
  targetSets: number | null;
  targetRepsMin: number | null;
  targetRepsMax: number | null;
  targetWeightKg: number | null;
  notes: string | null;
  priority?: 'core' | 'additional' | 'optional';
}

export type WorkoutStatus = 'active' | 'completed';

export interface WorkoutSession extends TimestampedEntity {
  workoutDate: LocalDate;
  startedAt: ISODateTime | null;
  finishedAt: ISODateTime | null;
  status: WorkoutStatus;
  sourcePlanId: UUID | null;
  planNameSnapshot: string | null;
  notes: string | null;
  restEndsAt: ISODateTime | null;
  restPausedSeconds: number | null;
  restSelectedSeconds: number;
  availableMinutes?: 40 | 60 | 90;
}

export interface WorkoutExercise extends TimestampedEntity {
  workoutSessionId: UUID;
  exerciseId: UUID | null;
  exerciseIdSnapshot: UUID;
  exerciseNameSnapshot: string;
  muscleGroupSnapshot: MuscleGroup;
  targetSetsSnapshot: number | null;
  targetRepsMinSnapshot: number | null;
  targetRepsMaxSnapshot: number | null;
  targetWeightKgSnapshot: number | null;
  orderIndex: number;
  notes: string | null;
  equipmentKey?: string;
  feedbackReason?: FeedbackReason;
  recommendation?: TrainingRecommendation;
}

export interface SetEntry extends TimestampedEntity {
  workoutExerciseId: UUID;
  orderIndex: number;
  repetitions: number | null;
  weightKg: number | null;
  isCompleted: boolean;
  completedAt: ISODateTime | null;
  kind?: 'warmup' | 'working';
  rir?: number | null;
  pain?: boolean;
}

export type ScheduledWorkoutStatus = 'scheduled' | 'started' | 'completed' | 'cancelled';

export interface ScheduledWorkout extends TimestampedEntity {
  scheduledDate: LocalDate;
  reminderTime: string;
  planId: UUID;
  planNameSnapshot: string;
  status: ScheduledWorkoutStatus;
  linkedWorkoutSessionId: UUID | null;
  calendarExportedAt: ISODateTime | null;
}

export type ThemePreference = 'system' | 'light' | 'dark';

export interface AppSettings extends TimestampedEntity {
  defaultRestSeconds: number;
  lastScheduledReminderTime: string;
  theme: ThemePreference;
  displayName?: string;
  heightCm?: number | null;
  avatarDataUrl?: string | null;
  lastBackupAt: ISODateTime | null;
  storagePersistenceRequestedAt: ISODateTime | null;
  onboardingDismissed: boolean;
}

export interface AppMetadata extends TimestampedEntity {
  schemaVersion: number;
  appVersion: string;
  firstOpenedAt: ISODateTime;
  seedVersion: number;
}

export interface RestTimerState {
  selectedSeconds: number;
  remainingSeconds: number;
  isRunning: boolean;
  isPaused: boolean;
  isComplete: boolean;
}

export interface PlanExerciseDraft {
  id?: UUID;
  exerciseId: UUID;
  targetSets: number | null;
  targetRepsMin: number | null;
  targetRepsMax: number | null;
  targetWeightKg: number | null;
  notes: string | null;
  priority?: 'core' | 'additional' | 'optional';
}

export interface PlanWithExercises extends WorkoutPlan {
  exercises: Array<PlanExercise & { exercise: Exercise | null }>;
}

export interface WorkoutExerciseWithSets extends WorkoutExercise {
  sets: SetEntry[];
}

export interface WorkoutWithDetails extends WorkoutSession {
  exercises: WorkoutExerciseWithSets[];
}

export interface DatabaseSnapshot {
  exercises: Exercise[];
  workoutPlans: WorkoutPlan[];
  planExercises: PlanExercise[];
  workoutSessions: WorkoutSession[];
  workoutExercises: WorkoutExercise[];
  setEntries: SetEntry[];
  scheduledWorkouts: ScheduledWorkout[];
  appSettings: AppSettings[];
  appMetadata: AppMetadata[];
  userProfiles: UserProfile[];
  exercisePreferences: ExercisePreference[];
  bodyMeasurements: BodyMeasurement[];
}

export type FeedbackReason = 'sleep' | 'time' | 'equipment' | 'technique' | 'pain' | 'unknown';
export type Confidence = 'low' | 'medium' | 'high';
export interface UserProfile extends TimestampedEntity {
  goal: 'hypertrophy';
  experience: 'beginner' | 'experienced';
  daysPerWeek: number;
  availableMinutes: 40 | 60 | 90;
  equipment: 'gym' | 'limited';
  avoidAxialLoad: boolean;
  excludedExerciseIds: UUID[];
  medicalNotes: string;
  recommendationsEnabled: boolean;
  onboardingCompletedAt: ISODateTime;
}
export interface ExercisePreference extends TimestampedEntity {
  exerciseId: UUID;
  equipmentKey: string;
  incrementKg: number;
  recalibrateAfter: ISODateTime | null;
  painResolvedAt: ISODateTime | null;
}
export interface BodyMeasurement extends TimestampedEntity {
  date: LocalDate;
  weightKg: number;
}
export interface TrainingRecommendation {
  version: 1;
  state: 'calibration' | 'ready' | 'hold' | 'increase' | 'recalibrate' | 'pain';
  confidence: Confidence;
  exposures: number;
  weightKg: number | null;
  repetitions: number[];
  reason: string;
  askRir: boolean;
  reviewDue: boolean;
}

export type SnapshotTableName = keyof DatabaseSnapshot;
